import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Shield, Check, X, Search, RefreshCw, Loader2, Lock, Pencil, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  super_admin: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  owner:       "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  admin:       "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  teknisi:     "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  kepala_cabang: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
  customer_service: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
};
const DEFAULT_ROLE_COLOR = "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";

const CATEGORY_LABELS = {
  dashboard: "Dashboard",
  customers: "Pelanggan",
  billing: "Invoice & Pembayaran",
  devices: "Perangkat (Router/ONT)",
  monitoring: "Monitoring",
  vpn: "VPN",
  company: "Perusahaan",
  users: "User Management",
  roles: "Role & Permission",
  reports: "Laporan",
  radius: "RADIUS",
  superadmin: "Super Admin",
};

const RoleMatrix = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [search, setSearch] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [savingCell, setSavingCell] = useState(null); // `${roleId}::${permId}`

  // Permission to edit: roles.manage or super_admin
  const canEdit = useMemo(() => {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    const perms = user.permissions || [];
    return perms.includes("*") || perms.includes("roles.manage");
  }, [user]);

  const fetchData = async (showToast = false) => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [rolesRes, permRes] = await Promise.all([
        axios.get(`${API_URL}/api/roles`, { headers }),
        axios.get(`${API_URL}/api/permissions`, { headers }),
      ]);
      if (rolesRes.data?.success) setRoles(rolesRes.data.data || []);
      const permPayload = permRes.data?.data?.permissions || permRes.data?.data || [];
      setPermissions(Array.isArray(permPayload) ? permPayload : []);
      if (showToast) toast.success("Matriks role di-refresh");
    } catch (err) {
      console.error("[RoleMatrix] fetch failed:", err);
      toast.error("Gagal memuat data role & permission");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(true);
  };

  const toggleCell = async (role, permId) => {
    if (!editMode || !canEdit) return;
    if (role.name === "super_admin") {
      toast.info("Super Admin tidak bisa diubah (system role)");
      return;
    }
    const roleId = role.id || role.name;
    const cellKey = `${roleId}::${permId}`;
    if (savingCell) return;

    const current = Array.isArray(role.permissions) ? role.permissions : [];
    const isAllowed = current.includes("*") || current.includes(permId);
    let newPerms;
    if (isAllowed) {
      newPerms = current.filter((p) => p !== permId && p !== "*");
    } else {
      newPerms = [...current.filter((p) => p !== "*"), permId];
    }

    // Optimistic update
    setRoles((prev) =>
      prev.map((r) => ((r.id || r.name) === roleId ? { ...r, permissions: newPerms } : r))
    );
    setSavingCell(cellKey);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(
        `${API_URL}/api/roles/${roleId}`,
        { permissions: newPerms },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        toast.success(`${role.display_name || role.name}: ${isAllowed ? "denied" : "granted"} ${permId}`, { duration: 1500 });
      } else {
        throw new Error(res.data?.message || "Update failed");
      }
    } catch (err) {
      console.error("[RoleMatrix] toggle failed:", err);
      toast.error(`Gagal update: ${err.response?.data?.detail || err.message}`);
      // Rollback
      setRoles((prev) =>
        prev.map((r) => ((r.id || r.name) === roleId ? { ...r, permissions: current } : r))
      );
    } finally {
      setSavingCell(null);
    }
  };

  const groupedPermissions = useMemo(() => {
    const groups = {};
    permissions.forEach((p) => {
      const cat = p.category || "lain";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });
    return groups;
  }, [permissions]);

  const rolePermSets = useMemo(() => {
    const map = {};
    roles.forEach((r) => {
      const perms = Array.isArray(r.permissions) ? r.permissions : [];
      map[r.id || r.name] = {
        hasWildcard: perms.includes("*"),
        set: new Set(perms),
      };
    });
    return map;
  }, [roles]);

  const isAllowed = (roleKey, permId) => {
    const entry = rolePermSets[roleKey];
    if (!entry) return false;
    if (entry.hasWildcard) return true;
    return entry.set.has(permId);
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return groupedPermissions;
    const q = search.toLowerCase();
    const result = {};
    Object.entries(groupedPermissions).forEach(([cat, perms]) => {
      const matched = perms.filter(
        (p) =>
          (p.display_name || p.name || "").toLowerCase().includes(q) ||
          (p.name || "").toLowerCase().includes(q) ||
          cat.toLowerCase().includes(q)
      );
      if (matched.length > 0) result[cat] = matched;
    });
    return result;
  }, [groupedPermissions, search]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600 dark:text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="role-matrix-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            Role Permission Matrix
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {editMode
              ? "Mode Edit aktif — klik cell ✓/✗ untuk toggle"
              : "Referensi siapa boleh melakukan apa — dinamis dari konfigurasi RBAC backend"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
            <Input
              placeholder="Cari permission atau kategori…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="role-matrix-search"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-10"
            data-testid="role-matrix-refresh"
            title="Refresh data"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
          {canEdit && (
            <Button
              variant={editMode ? "default" : "outline"}
              size="sm"
              onClick={() => setEditMode((v) => !v)}
              className={`h-10 gap-2 ${editMode ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
              data-testid="role-matrix-edit-toggle"
            >
              {editMode ? <><Save className="w-4 h-4" />Selesai</> : <><Pencil className="w-4 h-4" />Edit</>}
            </Button>
          )}
        </div>
      </div>

      {editMode && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
          <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Mode Edit aktif.</strong> Klik cell mana saja untuk mengubah permission role. Perubahan langsung tersimpan ke backend. Role <strong>Super Admin</strong> dikunci (tidak bisa diubah).
          </div>
        </div>
      )}

      <Card className="border-0 shadow-lg">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="role-matrix-table">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide min-w-[280px]">
                    Permission
                  </th>
                  {roles.map((r) => (
                    <th
                      key={r.id || r.name}
                      className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wide min-w-[130px]"
                    >
                      <div className="flex items-center justify-center gap-1">
                        <Badge className={`${ROLE_COLORS[r.name] || DEFAULT_ROLE_COLOR} border-0 font-semibold whitespace-nowrap`}>
                          {r.display_name || r.name}
                        </Badge>
                        {r.name === "super_admin" && editMode && <Lock className="w-3 h-3 text-slate-400 dark:text-slate-500" title="System role - locked" />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(filtered).length === 0 && (
                  <tr>
                    <td colSpan={roles.length + 1} className="text-center py-8 text-slate-400 dark:text-slate-500">
                      Tidak ada permission yang cocok dengan "{search}"
                    </td>
                  </tr>
                )}
                {Object.entries(filtered).map(([cat, perms]) => (
                  <React.Fragment key={cat}>
                    <tr className="bg-indigo-50 dark:bg-indigo-900/20">
                      <td
                        colSpan={roles.length + 1}
                        className="py-2 px-4 text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300"
                      >
                        {CATEGORY_LABELS[cat] || cat}
                      </td>
                    </tr>
                    {perms.map((p, ri) => {
                      const permId = p.id || p.name;
                      return (
                        <tr
                          key={permId}
                          className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${
                            ri % 2 === 0 ? "" : "bg-slate-50/50 dark:bg-slate-800/20"
                          }`}
                        >
                          <td className="py-2.5 px-4">
                            <div className="text-sm text-slate-700 dark:text-slate-200">{p.display_name || p.name}</div>
                            {p.name && p.display_name && p.name !== p.display_name && (
                              <div className="text-[10px] text-slate-400 font-mono mt-0.5 dark:text-slate-500">{p.name}</div>
                            )}
                          </td>
                          {roles.map((r) => {
                            const roleKey = r.id || r.name;
                            const allowed = isAllowed(roleKey, permId);
                            const cellKey = `${roleKey}::${permId}`;
                            const isSaving = savingCell === cellKey;
                            const isLocked = r.name === "super_admin";
                            const interactive = editMode && canEdit && !isLocked && !isSaving;
                            return (
                              <td key={roleKey} className="py-2.5 px-4">
                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    onClick={() => toggleCell(r, permId)}
                                    disabled={!interactive}
                                    data-testid={`cell-${roleKey}-${permId}`}
                                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-all ${
                                      allowed
                                        ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300"
                                        : "bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-300"
                                    } ${
                                      interactive
                                        ? "hover:scale-110 hover:shadow-md cursor-pointer ring-2 ring-transparent hover:ring-indigo-300"
                                        : "cursor-default"
                                    } ${isSaving ? "opacity-50" : ""} ${isLocked && editMode ? "opacity-60" : ""}`}
                                    title={
                                      isLocked
                                        ? "Super Admin - dikunci"
                                        : interactive
                                        ? `Klik untuk ${allowed ? "cabut" : "beri"} permission`
                                        : allowed ? "Diizinkan" : "Tidak diizinkan"
                                    }
                                  >
                                    {isSaving ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : allowed ? (
                                      <Check className="w-4 h-4" strokeWidth={3} />
                                    ) : (
                                      <X className="w-4 h-4" strokeWidth={3} />
                                    )}
                                  </button>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm bg-slate-50 dark:bg-slate-800/30">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400">
                <Check className="w-3.5 h-3.5" strokeWidth={3} />
              </div>
              <span className="text-slate-600 dark:text-slate-300">Diizinkan</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-500 dark:bg-red-900/40">
                <X className="w-3.5 h-3.5" strokeWidth={3} />
              </div>
              <span className="text-slate-600 dark:text-slate-300">Tidak diizinkan</span>
            </div>
            <div className="text-xs text-slate-400 italic ml-auto dark:text-slate-500">
              {roles.length} role × {permissions.length} permission
              {!canEdit && <span className="ml-2">— View only (butuh roles.manage)</span>}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RoleMatrix;
